import { toast } from "sonner";
import {
  Ban,
  Loader2,
  RotateCcw,
  Send,
  ShieldCheck,
  UserCog,
  UserPlus,
  UserX,
} from "lucide-react";

import {
  type CarrierPortalInvite,
  type CarrierPortalMember,
  type CarrierPortalMembership,
  type CarrierPortalMembershipStatus,
  type CarrierPortalRole,
  type EditableCarrierPortalRole,
  useDeleteCarrierPortalInvite,
  useGetCarrierPortalTeam,
  useResendCarrierPortalInvite,
  useUpdateCarrierPortalMember,
  useUpdateCarrierPortalMembership,
} from "@/api/partner-carriers";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ALL_CENTERS = "__ALL_CENTERS__";

function roleLabel(role: CarrierPortalRole) {
  if (role === "CARRIER_OWNER") return "Carrier Admin";
  if (role === "CARRIER_DISPATCHER") return "Dispatcher";
  return "Operator";
}

function formatDate(value: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function MembershipStatusBadge({
  membership,
}: {
  membership: CarrierPortalMembership;
}) {
  const isActive = membership.status === "ACTIVE";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge
        variant="outline"
        className={
          isActive
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-amber-200 bg-amber-50 text-amber-700"
        }
      >
        {isActive ? "Membership active" : "Membership disabled"}
      </Badge>
      {!isActive && membership.disabledAt && (
        <span className="text-xs text-muted-foreground">
          Disabled {formatDate(membership.disabledAt)}
        </span>
      )}
    </div>
  );
}

function MembershipControls({
  carrierId,
  member,
  membership,
  dispatchCenters,
  onRoleChange,
  onScopeChange,
  onStatusChange,
  isMutating,
}: {
  carrierId: string;
  member: CarrierPortalMember;
  membership: CarrierPortalMembership;
  dispatchCenters: Array<{ id: string; name: string }>;
  onRoleChange: (membershipId: string, role: EditableCarrierPortalRole) => void;
  onScopeChange: (
    membershipId: string,
    dispatchCenterId: string | null
  ) => void;
  onStatusChange: (
    membershipId: string,
    status: CarrierPortalMembershipStatus
  ) => void;
  isMutating: boolean;
}) {
  const nextStatus: CarrierPortalMembershipStatus =
    membership.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
  const statusActionLabel =
    nextStatus === "DISABLED" ? "Disable membership" : "Enable membership";

  if (membership.role === "OPERATOR") {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="outline">Operator</Badge>
          <MembershipStatusBadge membership={membership} />
          <span className="text-muted-foreground">
            {membership.dispatchCenter?.name ?? "No dispatch center"}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={isMutating}
          onClick={() => onStatusChange(membership.id, nextStatus)}
        >
          {nextStatus === "DISABLED" ? (
            <Ban className="h-4 w-4" />
          ) : (
            <RotateCcw className="h-4 w-4" />
          )}
          {statusActionLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <MembershipStatusBadge membership={membership} />
        <Button
          variant="outline"
          size="sm"
          disabled={isMutating}
          onClick={() => onStatusChange(membership.id, nextStatus)}
        >
          {nextStatus === "DISABLED" ? (
            <Ban className="h-4 w-4" />
          ) : (
            <RotateCcw className="h-4 w-4" />
          )}
          {statusActionLabel}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-[160px_1fr]">
        <select
          aria-label={`${member.email} role`}
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={membership.role}
          onChange={(event) =>
            onRoleChange(
              membership.id,
              event.target.value as EditableCarrierPortalRole
            )
          }
        >
          <option value="CARRIER_OWNER">Carrier Admin</option>
          <option value="CARRIER_DISPATCHER">Dispatcher</option>
        </select>
        <select
          aria-label={`${member.email} dispatch center`}
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={membership.dispatchCenterId ?? ALL_CENTERS}
          onChange={(event) =>
            onScopeChange(
              membership.id,
              event.target.value === ALL_CENTERS ? null : event.target.value
            )
          }
        >
          <option value={ALL_CENTERS}>All dispatch centers</option>
          {dispatchCenters.map((center) => (
            <option key={`${carrierId}:${center.id}`} value={center.id}>
              {center.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function MembersTable({
  carrierId,
  members,
  dispatchCenters,
  onRoleChange,
  onScopeChange,
  onStatusChange,
  onActiveChange,
  isMutating,
}: {
  carrierId: string;
  members: CarrierPortalMember[];
  dispatchCenters: Array<{ id: string; name: string }>;
  onRoleChange: (membershipId: string, role: EditableCarrierPortalRole) => void;
  onScopeChange: (
    membershipId: string,
    dispatchCenterId: string | null
  ) => void;
  onStatusChange: (
    membershipId: string,
    status: CarrierPortalMembershipStatus
  ) => void;
  onActiveChange: (carrierUserId: string, active: boolean) => void;
  isMutating: boolean;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <UserCog className="h-4 w-4" />
        <h3 className="text-sm font-semibold">Portal accounts</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Account</TableHead>
            <TableHead className="w-28">State</TableHead>
            <TableHead>Role, scope, and membership</TableHead>
            <TableHead className="w-32" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground">
                No carrier portal accounts yet.
              </TableCell>
            </TableRow>
          )}
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell>
                <div className="font-medium">{member.name}</div>
                <div className="text-xs text-muted-foreground">
                  {member.email}
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={
                    member.active
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-gray-200 bg-gray-50 text-gray-600"
                  }
                >
                  {member.active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="space-y-2">
                  {member.memberships.map((membership) => (
                    <MembershipControls
                      key={membership.id}
                      carrierId={carrierId}
                      member={member}
                      membership={membership}
                      dispatchCenters={dispatchCenters}
                      onRoleChange={onRoleChange}
                      onScopeChange={onScopeChange}
                      onStatusChange={onStatusChange}
                      isMutating={isMutating}
                    />
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isMutating}
                  onClick={() => onActiveChange(member.id, !member.active)}
                >
                  {member.active ? (
                    <>
                      <UserX className="h-4 w-4" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" />
                      Reactivate
                    </>
                  )}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function InvitesTable({
  invites,
  onResend,
  onRevoke,
  isMutating,
}: {
  invites: CarrierPortalInvite[];
  onResend: (inviteId: string) => void;
  onRevoke: (inviteId: string) => void;
  isMutating: boolean;
}) {
  const pending = invites.filter((invite) => !invite.acceptedAt);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" />
        <h3 className="text-sm font-semibold">Pending invites</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Dispatch center</TableHead>
            <TableHead>Expires</TableHead>
            <TableHead className="w-48" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pending.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                No pending invites.
              </TableCell>
            </TableRow>
          )}
          {pending.map((invite) => (
            <TableRow key={invite.id}>
              <TableCell className="font-medium">{invite.email}</TableCell>
              <TableCell>{roleLabel(invite.role)}</TableCell>
              <TableCell>
                {invite.dispatchCenter?.name ?? "All dispatch centers"}
              </TableCell>
              <TableCell>{formatDate(invite.expiresAt)}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => onResend(invite.id)}
                  >
                    <Send className="h-4 w-4" />
                    Resend
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => onRevoke(invite.id)}
                  >
                    Revoke
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

interface CarrierPortalTeamDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite?: () => void;
  carrierId: string;
  carrierName: string;
}

export function CarrierPortalTeamDialog({
  open,
  onClose,
  onInvite,
  carrierId,
  carrierName,
}: CarrierPortalTeamDialogProps) {
  const team = useGetCarrierPortalTeam(open ? carrierId : null);
  const dispatchCenters = useGetDispatchCenters();
  const carrierDispatchCenters =
    dispatchCenters.data
      ?.filter((center) => center.pickupProviderCarrier.id === carrierId)
      .map((center) => ({ id: center.id, name: center.name })) ?? [];

  const updateMember = useUpdateCarrierPortalMember({
    onSuccess: () => toast.success("Portal account updated"),
    onError: () => toast.error("Failed to update portal account"),
  });
  const updateMembership = useUpdateCarrierPortalMembership({
    onSuccess: () => toast.success("Membership updated"),
    onError: () => toast.error("Failed to update membership"),
  });
  const deleteInvite = useDeleteCarrierPortalInvite({
    onSuccess: () => toast.success("Invite revoked"),
    onError: () => toast.error("Failed to revoke invite"),
  });
  const resendInvite = useResendCarrierPortalInvite({
    onSuccess: () => toast.success("Invite resent"),
    onError: () => toast.error("Failed to resend invite"),
  });

  const isMutating =
    updateMember.isPending ||
    updateMembership.isPending ||
    deleteInvite.isPending ||
    resendInvite.isPending;

  const handleRoleChange = (
    membershipId: string,
    role: EditableCarrierPortalRole
  ) => {
    updateMembership.mutate({
      carrierId,
      membershipId,
      body: { role },
    });
  };

  const handleScopeChange = (
    membershipId: string,
    dispatchCenterId: string | null
  ) => {
    updateMembership.mutate({
      carrierId,
      membershipId,
      body: { dispatchCenterId },
    });
  };

  const handleStatusChange = (
    membershipId: string,
    status: CarrierPortalMembershipStatus
  ) => {
    updateMembership.mutate({
      carrierId,
      membershipId,
      body: { status },
    });
  };

  const handleActiveChange = (carrierUserId: string, active: boolean) => {
    updateMember.mutate({
      carrierId,
      carrierUserId,
      body: { active },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <DialogTitle>Carrier portal users - {carrierName}</DialogTitle>
              <DialogDescription>
                Manage carrier portal accounts, membership roles,
                dispatch-center scope, and pending invites for this carrier.
              </DialogDescription>
            </div>
            {onInvite && (
              <Button type="button" onClick={onInvite} className="sm:shrink-0">
                <UserPlus className="h-4 w-4" />
                Invite user
              </Button>
            )}
          </div>
        </DialogHeader>

        {team.isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading carrier portal users...
          </div>
        )}

        {team.error && (
          <div className="text-sm text-destructive">
            Failed to load carrier portal users.
          </div>
        )}

        {team.data && (
          <div className="space-y-8">
            <MembersTable
              carrierId={carrierId}
              members={team.data.members}
              dispatchCenters={carrierDispatchCenters}
              onRoleChange={handleRoleChange}
              onScopeChange={handleScopeChange}
              onStatusChange={handleStatusChange}
              onActiveChange={handleActiveChange}
              isMutating={isMutating}
            />
            <InvitesTable
              invites={team.data.invites}
              onResend={(inviteId) =>
                resendInvite.mutate({ carrierId, inviteId })
              }
              onRevoke={(inviteId) =>
                deleteInvite.mutate({ carrierId, inviteId })
              }
              isMutating={isMutating}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
